import { useState } from 'react';
import cn from 'classnames';
import { useLogoutCustomer } from 'hooks';
import ArrowDown from 'components/shared/arrow-down';
import Icon from 'components/shared/icon';
import RichText from 'components/shared/richtext';
import { formatPriceByStoreLocale } from 'lib/shopify';
import Modal from '../modal/modal';
import styles from '../salonAccount.module.scss';
import NoKickbackfeePopup from '../noKickbackfeePopup';

const Summary = ({
  locale,
  payload: {
    customer,
    salon,
    salons,
    isMultiSalon,
    dropdownOpen,
    handleDropdown,
    handlePickSalon,
    handleRefresh,
    canRequestPayout,
    showInfoBoxReturn,
    setShowInfoBoxReturn,
    fees,
  },
  global,
}) => {
  const [modalOpen, setModalOpen] = useState(false);
  const [payoutError, setPayoutError] = useState(false);
  const { total, payable, paid, awaitingReturnsPeriod } = fees;
  const [logoutCustomer] = useLogoutCustomer(locale);

  const handleModal = () => {
    setModalOpen(!modalOpen);
    if (payoutError) handleRefresh();
  };

  const handleUpdateMailChimp = async () => {
    const url = '/api/mailchimp/edit-mailchimp-salon-payout';
    const response = await fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        salonNo: salon.salonNo,
      }),
    });
    const { result } = await response.json();
    return result;
  };

  const handleRequestPayout = async () => {
    const url = '/api/request-payout';
    const payload = {
      salonNo: salon.salonNo,
      salonName: salon.salonName,
      amount: payable,
      email: salon.email,
      locale,
    };
    const response = await fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(payload),
    });
    const { result } = await response.json();

    if (result === 'Success') {
      handleModal();
      handleRefresh();
      handleUpdateMailChimp();
    } else {
      setPayoutError(true);
    }
  };

  const getSummaryText = () => {
    const firstPart = `${global.account['salonAccount-Summary']?.text?.split('{amountBack}')[0]}`;
    const secondPart = `${global.account['salonAccount-Summary']?.text
      ?.split('{amountBack}')[1]
      ?.replace('{amountTotal}', '')}`;
    return (
      <>
        {firstPart}{' '}
        <span style={{ fontWeight: 'bold' }}>
          {formatPriceByStoreLocale({
            locale,
            price: paid,
          })}
        </span>{' '}
        {secondPart}{' '}
        <span style={{ fontWeight: 'bold' }}>
          {formatPriceByStoreLocale({
            locale,
            price: total,
          })}
        </span>
      </>
    );
  };

  const logoutText = global.account['salonAccount-Logout']?.text.replace(
    '{user}',
    `${customer.firstName}`
  );

  return (
    <>
      <Modal
        modalOpen={modalOpen}
        handleModal={handleModal}
        handleRequestPayout={handleRequestPayout}
        payoutError={payoutError}
        fees={fees}
        global={global}
        locale={locale}
      />
      <div className={styles.summary}>
        <div className={styles.header}>
          <div>{global.account['salonAccount-Title']?.text}</div>
          <div
            className={styles.logout}
            onClick={() => {
              logoutCustomer(customer.customerAccessToken.accessToken);
            }}
            onKeyDown={() => {
              logoutCustomer(customer.customerAccessToken.accessToken);
            }}
            tabIndex={0}
            role="button"
          >
            {logoutText}
          </div>
        </div>
        <div className={styles.description}>{global.account['salonAccount-Description']?.text}</div>
        {/** Salon Picker */}{' '}
        <div
          className={styles.salonPicker}
          onClick={handleDropdown}
          onKeyDown={handleDropdown}
          tabIndex={0}
          role="button"
        >
          <div className={cn(styles.info, { [styles.clickable]: isMultiSalon })}>
            <div className={styles.salonName}>{salon.salonName}</div>
            <div className={styles.salonAddress}>
              {salon.AddressLine2} {salon.ZipCode || salon.zipCode} {salon.city}
            </div>
          </div>
          {isMultiSalon ? (
            <div className={styles.icon}>
              <ArrowDown width="22px" height="22px" />
            </div>
          ) : null}
        </div>
        <div className={styles.dropdownContainer}>
          <div className={cn(styles.dropdown, { [styles.dropdownOpen]: dropdownOpen })}>
            {salons.map(newSalon => (
              <div
                className={cn(styles.option, {
                  [styles.sameAsSelected]: salon.salonNo === newSalon.salonNo,
                })}
                onClick={() => handlePickSalon(newSalon)}
                onKeyDown={() => handlePickSalon(newSalon)}
                tabIndex={0}
                role="button"
              >
                <div className={styles.info}>
                  <div className={styles.salonName}>{newSalon.salonName}</div>
                  <div className={styles.salonAddress}>
                    {salon.AddressLine2} {newSalon.ZipCode || newSalon.zipCode} {newSalon.city}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
        <div className={styles.breakdown}>
          <div className={styles.amountBox}>
            <div className={styles.label}>{global.tables['tables-Payable']?.text}</div>
            <div className={styles.amount}>
              {formatPriceByStoreLocale({
                locale,
                price: payable,
              })}
            </div>
          </div>
          <div className={styles.amountBox}>
            <div className={styles.label}>
              {global.tables['tables-AwaitingReturnPeriod']?.text}
              <span
                onMouseEnter={() => setShowInfoBoxReturn(!showInfoBoxReturn)}
                onMouseLeave={() => setShowInfoBoxReturn(!showInfoBoxReturn)}
              >
                {' '}
                <Icon type="info" />
              </span>{' '}
            </div>
            <div className={styles.amount}>
              {formatPriceByStoreLocale({
                locale,
                price: awaitingReturnsPeriod,
              })}
            </div>
          </div>
          <div className={cn(styles.infobox, { [styles.showInfoBoxReturn]: showInfoBoxReturn })}>
            <div className={styles.triangle} />
            <div className={styles.box}>
              <RichText text={global.account['salonAccount-ReturnTimeExplanation']?.text.json} />
            </div>
          </div>
          <button
            type="button"
            className={cn(styles.button, { [styles.disabled]: !canRequestPayout })}
            onClick={handleModal}
          >
            {global.account['salonAccount-Button']?.text}
          </button>
          <RichText
            text={global?.account?.['salonAccount-AdditionalInfo'].text?.json}
            className={styles.additionalInfo}
          />
          <div className={styles.overallStatus}>{getSummaryText()}</div>
        </div>
      </div>
      <NoKickbackfeePopup globalModules={global} fees={fees} />
    </>
  );
};

export default Summary;
